import { PastilleNoyau } from '@/components/noyaux/pastille-noyau';
import { cn } from '@/lib/utils';
import { Checkbox } from '@pms-digital/design-system';
import { NOYAUX, NOYAU_CODES, type NoyauCode } from '@rosetta/shared';
import { useEffect, useRef, useState } from 'react';
import { IconeHistorique } from './icone-historique';

interface FiltreNoyauxProps {
  /** The checked noyaux; none means no filter. */
  valeur: readonly NoyauCode[];
  onChange: (valeur: NoyauCode[]) => void;
}

/** "tous les noyaux", the code of the only one checked, or "2 noyaux". */
export function libelleFiltre(valeur: readonly NoyauCode[]): string {
  const [premier] = valeur;
  if (premier === undefined) return 'tous les noyaux';
  return valeur.length === 1 ? premier : `${valeur.length} noyaux`;
}

/** "souverain magicien": the long name in the middle of a menu line. */
const nomNoyau = (code: NoyauCode): string => `${NOYAUX[code].famille} ${NOYAUX[code].variante}`;

/**
 * The noyau filter of the history: a button that names the choice ("tous les noyaux",
 * "GSP", "2 noyaux", framed in bleu nuit once a filter is on) and opens a menu of six
 * checkboxes with "tout afficher" and "fermer"; a click anywhere on a line ticks its box. The menu also closes with Escape and a
 * click anywhere else (the document listens, wherever the focus is). Closed by the keyboard
 * ("fermer", Escape), it hands the focus back to its button, which "fermer" would otherwise
 * take away with it; and it closes when a Tab leaves it (S14, #5).
 */
export function FiltreNoyaux({ valeur, onChange }: FiltreNoyauxProps) {
  const [ouvert, setOuvert] = useState(false);
  const racine = useRef<HTMLDivElement>(null);
  const declencheur = useRef<HTMLButtonElement>(null);
  const actif = valeur.length > 0;

  /** Closes the menu and puts the keyboard back on its button, where it was opened from. */
  function fermerEtRevenir() {
    setOuvert(false);
    declencheur.current?.focus();
  }

  // Listening to the document is not a fetch: it is how a menu notices a click outside.
  useEffect(() => {
    if (!ouvert) return;
    const surClic = (evenement: PointerEvent) => {
      if (evenement.target instanceof Node && !racine.current?.contains(evenement.target)) {
        setOuvert(false);
      }
    };
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape') return;
      setOuvert(false);
      declencheur.current?.focus();
    };
    document.addEventListener('pointerdown', surClic);
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('pointerdown', surClic);
      document.removeEventListener('keydown', surTouche);
    };
  }, [ouvert]);

  function basculer(code: NoyauCode) {
    const suivants = new Set(valeur);
    if (!suivants.delete(code)) suivants.add(code);
    // The charter order, whatever the order of the clicks.
    onChange(NOYAU_CODES.filter((candidat) => suivants.has(candidat)));
  }

  return (
    <div
      ref={racine}
      className="relative"
      onBlur={(evenement) => {
        // The focus went to another control: the menu is left behind. (Not to nowhere, which
        // is a click on the page or another window: the click handler above deals with those;
        // nor to an element out of the Tab order, such as the `main` of the shell, which takes
        // the focus of a click on a line of the menu.)
        const arrivee = evenement.relatedTarget;
        if (
          arrivee instanceof HTMLElement &&
          arrivee.tabIndex >= 0 &&
          !evenement.currentTarget.contains(arrivee)
        ) {
          setOuvert(false);
        }
      }}
    >
      <button
        ref={declencheur}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        onClick={() => setOuvert(!ouvert)}
        className={cn(
          'inline-flex h-10 shrink-0 items-center gap-2 rounded-md border bg-white px-3 font-semibold text-black text-small hover:bg-gray-100',
          actif ? 'border-bleu-nuit' : 'border-gray-300',
        )}
      >
        <IconeHistorique name="filter_list" />
        {libelleFiltre(valeur)}
        <IconeHistorique name="expand_more" />
      </button>
      {ouvert ? (
        // biome-ignore lint/a11y/useSemanticElements: a popover under its button, not a modal <dialog>
        <div
          role="dialog"
          aria-label="filtrer par noyau"
          className="absolute top-12 left-0 z-20 w-[280px] rounded-md border border-gray-200 bg-white py-2 shadow-md"
        >
          <div className="px-4 py-2 text-caption text-gray-600 leading-[1.4]">
            Afficher les traductions vers
          </div>
          {NOYAU_CODES.map((code) => {
            const coche = valeur.includes(code);
            return (
              // A wider target for the pointer only: the keyboard has the checkbox itself.
              // biome-ignore lint/a11y/useKeyWithClickEvents: the checkbox inside is the keyboard control
              <div
                key={code}
                data-ligne-case=""
                onClick={(evenement) => {
                  // The box and its label tick themselves; the rest of the line ticks the box.
                  if (
                    evenement.target instanceof Element &&
                    evenement.target.closest('input, label')
                  ) {
                    return;
                  }
                  basculer(code);
                }}
                className="flex h-10 items-center px-4 hover:bg-gray-100"
              >
                <Checkbox
                  checked={coche}
                  onCheckedChange={() => basculer(code)}
                  label={
                    <span className="inline-flex items-center gap-3">
                      <PastilleNoyau noyau={code} className="size-2.5" />
                      <span className="w-[34px] font-bold text-small tracking-[1px]">{code}</span>
                      <span className="font-light text-gray-700 text-small">{nomNoyau(code)}</span>
                    </span>
                  }
                />
              </div>
            );
          })}
          <div className="mt-2 flex justify-between border-gray-100 border-t px-4 pt-2 pb-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="-mx-2 rounded-md px-2 py-1 font-semibold text-bleu-nuit text-small hover:bg-gray-100"
            >
              tout afficher
            </button>
            <button
              type="button"
              onClick={fermerEtRevenir}
              className="-mx-2 rounded-md px-2 py-1 font-semibold text-black text-small hover:bg-gray-100"
            >
              fermer
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
